import { getAuthorizationHeader } from "@brandclub/common-ui";
import { axiosRetailerSync } from "../../utils/axios";
import type { CheckoutSession } from "../../components/Checkout/components/CheckoutSimplified/types";

export interface ApplyPromoCodeRequest {
  code: string;
  orderId: string; // This is the checkout session ID
}

export interface ApplyPromoCodeResponse {
  success: boolean;
  code: string;
  description: string;
  discountAmount: number;
  errorMessage?: string;
  session?: CheckoutSession;
}

export interface RemovePromoCodeRequest {
  code: string;
  orderId: string; // This is the checkout session ID
}

export interface RemovePromoCodeResponse {
  success: boolean;
  session?: CheckoutSession;
}

interface ValidateCouponResponse {
  valid: boolean;
  code?: string;
  description?: string;
  discountAmount?: number;
  errorMessage?: string;
}

// ============================================================
// API FUNCTIONS
// ============================================================

/**
 * Validate a coupon code without applying it
 */
export async function validatePromoCode(
  code: string,
  sessionId: string
): Promise<ValidateCouponResponse> {
  try {
    const headers = await getAuthorizationHeader();
    const response = await axiosRetailerSync.post<ValidateCouponResponse>(
      `/checkout_sessions/${sessionId}/coupon_code/validate`,
      { code },
      { headers }
    );
    return response.data;
  } catch (error: any) {
    console.error("Failed to validate promo code:", error);
    return {
      valid: false,
      errorMessage:
        error?.response?.data?.message || "Failed to validate coupon code",
    };
  }
}

/**
 * Apply a coupon code to the checkout session
 */
export async function applyPromoCode(
  request: ApplyPromoCodeRequest
): Promise<ApplyPromoCodeResponse> {
  const { code, orderId: sessionId } = request;

  try {
    const headers = await getAuthorizationHeader();
    const response = await axiosRetailerSync.post(
      `/checkout_sessions/${sessionId}/coupon_code`,
      { code },
      { headers }
    );

    // Extract coupon info from the returned session
    const session = response.data;
    const appliedCoupon = session.applied_coupon;

    if (appliedCoupon) {
      return {
        success: true,
        code: appliedCoupon.code,
        description: appliedCoupon.description,
        discountAmount:
          appliedCoupon.calculated_discount ||
          appliedCoupon.calculatedDiscount ||
          0,
        session, // Return full session for totals update
      };
    }

    return {
      success: false,
      code,
      description: "",
      discountAmount: 0,
      errorMessage: "Failed to apply coupon code",
    };
  } catch (error: any) {
    console.error("Failed to apply promo code:", error);
    return {
      success: false,
      code,
      description: "",
      discountAmount: 0,
      errorMessage:
        error?.response?.data?.message || "Invalid or expired promotion code",
    };
  }
}

/**
 * Remove a coupon code from the checkout session
 */
export async function removePromoCode(
  request: RemovePromoCodeRequest
): Promise<RemovePromoCodeResponse> {
  const { orderId: sessionId } = request;

  try {
    const headers = await getAuthorizationHeader();
    const response = await axiosRetailerSync.delete(
      `/checkout_sessions/${sessionId}/coupon_code`,
      { headers }
    );
    return { success: true, session: response.data };
  } catch (error: any) {
    console.error("Failed to remove promo code:", error);
    return { success: false };
  }
}
